import React, { useCallback, useEffect, useState } from 'react';
import { Row } from 'reactstrap';
import Pagination from 'components/Pagination';
import FilterAccountsForm from './components/FilterAccountsForm';
import ListAccountsTable from './components/ListAccountsTable';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useListAccounts } from 'views/CRM/Account/hooks/useListAccounts';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from '../../../../../../hooks/useMemorizeInputsFilters';
import { FlexTableSkeleton } from 'components/FlexTable/components/skeleton';

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Object} [style]
 * @property {Object} [headerStyle]
 * @property {boolean} [sort]
 * @property {Function} [formatter]
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @typedef {Object} Account
 * @property {string} id
 * @property {string} name
 * @property {string} phone
 * @property {string} createdAt
 * @property {string} createdByName
 * @property {string} lastUpdatedAt
 * @property {string} recordTypeId
 */

/**
 * @typedef {Object} RecordType
 * @property {string} id
 * @property {string} name
 */

/**
 * @param {Object} props
 * @param {string} props.selectedRecordTypeId
 * @param {Function} props.setSelectedRecordType
 * @param {RecordType[]} props.recordTypes
 */
const AccountsList = ({
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes
}) => {
  const [activePage, setActivePage] = useState(1);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  const {
    getAccounts,
    accounts,
    totalAccounts,
    isLoadingAccounts,
    clearAccounts
  } = useListAccounts({
    recordTypeId: selectedRecordTypeId,
    sortConfig
  });

  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.ACCOUNT,
    selectedRecordTypeId
  );
  const { getMemorizedFilters, memorizeFilters } = useMemorizeFilters(
    POSSIBLE_FILTERS_ENTITIES.CRM_ACCOUNT
  );

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    clearAccounts();
  }, [selectedRecordTypeId, clearAccounts]);

  useEffect(() => {
    const currentFilters = {
      ...getMemorizedFilters(),
      ...filters,
      sort: sortConfig
    };
    handleSearch(currentFilters);
  }, [sortConfig]);

  /**
   * Handle search with filters.
   * @param {Object} searchFilters
   */
  const handleSearch = useCallback(
    (searchFilters) => {
      const page = searchFilters?.page || 1;
      setActivePage(page);
      setFilters(searchFilters);
      getAccounts(page, searchFilters);
    },
    [getAccounts]
  );

  /**
   * Handle page change.
   * @param {number} newPage
   */
  const handlePageChange = (newPage) => {
    const currentFilters = getMemorizedFilters();
    memorizeFilters({ ...currentFilters, page: newPage });
    setActivePage(newPage);
    getAccounts(newPage, filters);
  };

  const reloadAccounts = useCallback(() => {
    getAccounts(1, {});
  }, [getAccounts]);

  return (
    <>
      <FilterAccountsForm
        handleSearch={handleSearch}
        customFields={customFields}
        isLoading={isLoadingAccounts}
        selectedRecordTypeId={selectedRecordTypeId}
        setSelectedRecordType={setSelectedRecordType}
        recordTypes={recordTypes}
        recordTypeId={selectedRecordTypeId}
        totalAccounts={totalAccounts}
        accounts={accounts}
      />

      <div className="d-block small px-2 mt-4 text-right">
        {totalAccounts} resultado(s) encontrado(s)
      </div>
      <Row className="mt-2">
        {isLoadingAccounts ? (
          <div className="col-12">
            <Row className="justify-content-md-center">
              <FlexTableSkeleton />
            </Row>
          </div>
        ) : (
          <ListAccountsTable
            accounts={accounts}
            reloadAccounts={reloadAccounts}
            customFields={customFields}
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        )}
      </Row>
      <Pagination
        activePage={activePage}
        totalItemsCount={totalAccounts}
        onChange={handlePageChange.bind(this)}
      />
    </>
  );
};

export default AccountsList;
